import type { AdminStoryDetail, AdminStoryStatus, AdminVersion, Lang } from '@fonus/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowLeft,
  CalendarClock,
  Check,
  Mic,
  RefreshCw,
  Rocket,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  AutoTextarea,
  Confirm,
  Field,
  inputClass,
  JobStatusBadge,
  ListSkeleton,
  StoryStatusBadge,
} from '../components/common.tsx';
import { ErrorCard } from '../components/ErrorCard.tsx';
import { ScriptEditor } from '../components/ScriptEditor.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs.tsx';
import { api } from '../lib/api.ts';
import { humanFromApi, humanFromJob } from '../lib/errors.ts';
import {
  credits,
  duration,
  fromLocalInput,
  LANG_LABEL,
  money,
  SOURCE_LABEL,
  tashkent,
  toLocalInput,
  usd,
} from '../lib/format.ts';
import { useReadiness, useSettings, useStory } from '../lib/queries.ts';
import { isStaleError } from '../lib/stale.ts';
import { go } from '../lib/router.ts';
import { cn, newKey } from '../lib/utils.ts';
import { flagText } from './Stories.tsx';

const STAGES: { status: AdminStoryStatus; label: string }[] = [
  { status: 'DRAFT', label: 'Черновик' },
  { status: 'IN_REVIEW', label: 'На проверке' },
  { status: 'SCHEDULED', label: 'Запланирована' },
  { status: 'PUBLISHED', label: 'Опубликована' },
];
const JOB_LABEL: Record<string, string> = {
  GENERATE_STORY: 'Текст и рецензия',
  TTS: 'Озвучка',
  UPLOAD_PROCESS: 'Обработка записи',
  DIRECT_TAGS: 'Теги Claude',
  TTS_PREVIEW: 'Пробная озвучка',
  AUDIO_PROCESS: 'Синхронизация текста',
};
const WORDS_MIN = 550;
const WORDS_MAX = 700;

function Lifecycle({ status }: { status: AdminStoryStatus }) {
  if (status === 'ARCHIVED') return <Badge>Архив</Badge>;
  const current = STAGES.findIndex((s) => s.status === status);
  return (
    <ol className="flex flex-wrap items-center gap-2" aria-label="Этапы сказки">
      {STAGES.map((s, i) => (
        <li key={s.status} className="flex items-center gap-2">
          <span
            className={cn(
              'flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-semibold',
              i < current && 'bg-ok/12 text-ok',
              i === current && 'bg-accent text-white',
              i > current && 'bg-fill text-muted',
            )}
            aria-current={i === current ? 'step' : undefined}
          >
            {i < current && <Check className="size-3.5" />}
            {s.label}
          </span>
          {i < STAGES.length - 1 && <span className="h-px w-4 bg-line" />}
        </li>
      ))}
    </ol>
  );
}

function VersionPanel({
  storyId,
  v,
  story,
  onSaved,
}: {
  storyId: string;
  v: AdminVersion;
  story: AdminStoryDetail;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState({ title: v.title, summary: v.summary, text: v.text });
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [confirmRevoice, setConfirmRevoice] = useState(false);
  const [revoiceKey] = useState(newKey);
  const [tab, setTab] = useState<'text' | 'script'>('text');
  const upload = v.audioSource === 'UPLOAD';
  const live = story.status === 'PUBLISHED';
  const editable = story.status !== 'ARCHIVED';
  const words = draft.text.split(/\s+/).filter(Boolean).length;
  const changed = draft.title !== v.title || draft.summary !== v.summary || draft.text !== v.text;
  const save = useMutation({
    mutationFn: () =>
      api<{ textChanged: boolean }>(`/admin/stories/${storyId}/versions/${v.lang}`, {
        method: 'PATCH',
        body: Object.fromEntries(
          Object.entries(draft).filter(([k, val]) => val !== v[k as keyof typeof draft]),
        ),
      }),
    onSuccess: (r) => {
      toast.success(r.textChanged ? 'Сохранено — переозвучьте этот язык' : 'Сохранено');
      onSaved();
    },
    onError: (e) => toast.error(humanFromApi(e).title),
  });
  const revoice = useMutation({
    mutationFn: () =>
      api(`/admin/stories/${storyId}/versions/${v.lang}/tts`, {
        method: 'POST',
        body: { idempotencyKey: revoiceKey },
      }),
    onSuccess: () => {
      toast.success(`Озвучка ${v.lang} запущена`);
      setConfirmRevoice(false);
      onSaved();
    },
  });

  const reprocess = useMutation({
    mutationFn: () =>
      api(`/admin/stories/${storyId}/versions/${v.lang}/upload`, {
        method: 'POST',
        body: { idempotencyKey: newKey() },
      }),
    onSuccess: () => {
      toast.success('Запись обрабатывается заново');
      onSaved();
    },
  });
  const align = useMutation({
    mutationFn: () =>
      api(`/admin/stories/${storyId}/versions/${v.lang}/align`, {
        method: 'POST',
        body: { idempotencyKey: newKey() },
      }),
    onSuccess: () => {
      toast.success('Сверяем аудио с текстом…');
      onSaved();
    },
  });
  const verify = useMutation({
    mutationFn: (verified: boolean) =>
      api<{ realignJobId: string | null }>(`/admin/stories/${storyId}/versions/${v.lang}/verify`, {
        method: 'POST',
        body: { verified },
      }),
    onSuccess: (r, verified) => {
      toast.success(
        !verified
          ? 'Отметка снята'
          : r.realignJobId
            ? 'Отмечено — обновляем метки слов по записи'
            : 'Текст отмечен как вычитанный',
      );
      onSaved();
    },
  });

  // Optimistic until the refreshed story arrives.
  const verifiedShown =
    verify.variables !== undefined && (verify.isPending || verify.isSuccess)
      ? verify.variables
      : v.textVerified;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-2 pt-5">
          {upload && (
            <p className="text-[14px]">
              <Badge tone="accent">аудио загружено вручную</Badge>{' '}
              <span className="text-muted">
                {v.upload?.fileName ?? 'запись'} ·{' '}
                {v.upload?.mode === 'audio'
                  ? 'текст распознан из записи'
                  : 'текст сверен с записью'}
                {v.upload?.sttUsd !== null && v.upload?.sttUsd !== undefined
                  ? ` · распознавание ${v.upload.sttUsd < 0.01 ? 'меньше $0,01' : usd(v.upload.sttUsd)}`
                  : ''}
              </span>
            </p>
          )}
          {v.audioUrl ? (
            <>
              <audio controls preload="none" src={v.audioUrl} className="w-full" />
              <p className="text-[13px] text-muted">
                {duration(v.durationSec)} · {v.ttsModel} · {credits(v.ttsCredits)}
                {v.audioUrlLow && (
                  <>
                    {' · '}
                    <a
                      className="text-accent"
                      href={v.audioUrlLow}
                      target="_blank"
                      rel="noreferrer"
                    >
                      лёгкая версия
                    </a>
                  </>
                )}
              </p>
            </>
          ) : (
            <p className="text-[14px] text-muted">
              {story.busy ? 'Озвучивается…' : 'Озвучки пока нет.'}
            </p>
          )}
          {v.audioOutdated && !upload && (
            <Badge tone="warn">Текст изменён после озвучки — переозвучьте</Badge>
          )}
          {upload && v.upload?.timingsStale && (
            <Badge tone="warn" className="self-start">
              Текст изменён — метки слов обновятся после «Текст вычитан ✓»
            </Badge>
          )}
          {editable && upload && !v.audioUrl && !story.busy && (
            <div>
              <Button
                variant="secondary"
                size="sm"
                disabled={reprocess.isPending}
                onClick={() => reprocess.mutate()}
              >
                <RefreshCw /> Обработать запись заново
              </Button>
            </div>
          )}
          {reprocess.error && <ErrorCard error={humanFromApi(reprocess.error)} />}
          {v.audioUrl && !v.audioOutdated && v.timedWords === 0 && !story.busy && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral">Нет меток слов — текст в приложении не подсвечивается</Badge>
              {editable && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={align.isPending}
                  onClick={() => align.mutate()}
                >
                  Синхронизировать текст
                </Button>
              )}
            </div>
          )}
          {align.error && <ErrorCard error={humanFromApi(align.error)} />}
          {editable && !upload && (
            <div>
              <Button
                variant="secondary"
                size="sm"
                disabled={story.busy || changed}
                onClick={() => setConfirmRevoice(true)}
              >
                <Mic /> Переозвучить (≈{credits(v.revoiceCredits)})
              </Button>
              {changed && (
                <span className="ml-2 text-[12px] text-muted">сначала сохраните текст</span>
              )}
            </div>
          )}
          {revoice.error && <ErrorCard error={humanFromApi(revoice.error)} />}
        </CardContent>
      </Card>

      {v.textSource === 'STT' && v.text.trim() !== '' && (
        <Card
          className={cn(
            !v.textVerified && v.proofread === 'required' && 'border-warn/40 bg-warn/[.05]',
          )}
        >
          <CardContent className="flex flex-col gap-2 pt-5">
            <label className="flex items-center gap-3 text-[16px] font-semibold">
              <input
                type="checkbox"
                className="size-5 accent-[#FF8A3D]"
                checked={verifiedShown}
                disabled={!editable || changed || verify.isPending}
                onChange={(e) => verify.mutate(e.target.checked)}
              />
              Текст вычитан ✓
            </label>
            <p className="text-[13px] text-muted">
              Текст распознан из записи — прочитайте его, сверяясь с аудио, и исправьте ошибки.{' '}
              {v.lang === 'uz'
                ? 'Без этой отметки сказку нельзя одобрить.'
                : 'Рекомендуется перед одобрением.'}{' '}
              Любая правка текста снимает отметку.
              {changed && ' Сначала сохраните правки.'}
            </p>
            {verify.error && <ErrorCard error={humanFromApi(verify.error)} />}
          </CardContent>
        </Card>
      )}

      {v.notes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Рецензия · {v.qualityScore ?? '—'}/100</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col gap-2">
              {v.notes.map((n, i) => (
                <li key={i}>
                  <label className="flex items-start gap-2 text-[15px]">
                    <input
                      type="checkbox"
                      className="mt-1 size-4 accent-[#FF8A3D]"
                      checked={checked.has(i)}
                      onChange={() => {
                        const next = new Set(checked);
                        if (next.has(i)) next.delete(i);
                        else next.add(i);
                        setChecked(next);
                      }}
                    />
                    <span className={checked.has(i) ? 'text-muted line-through' : ''}>{n}</span>
                  </label>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {!upload && (
        <Tabs value={tab} onValueChange={(x) => setTab(x as 'text' | 'script')}>
          <TabsList aria-label="Текст или сценарий">
            <TabsTrigger value="text">Текст</TabsTrigger>
            <TabsTrigger value="script">Сценарий озвучки{v.ttsScript ? ' ●' : ''}</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {tab === 'script' && !upload && (
        <Card>
          <CardContent className="pt-5">
            <ScriptEditor
              key={`${v.ttsScript ?? ''}-${v.text}`}
              storyId={storyId}
              v={v}
              editable={editable}
              busy={story.busy}
              onChanged={onSaved}
            />
          </CardContent>
        </Card>
      )}
      <Card className={cn(tab === 'script' && !upload && 'hidden')}>
        <CardContent className="flex flex-col gap-4 pt-5">
          {live && editable && (
            <Badge tone="warn" className="self-start">
              Уже в эфире — изменения увидят дети
            </Badge>
          )}
          <Field label="Название">
            <input
              className={inputClass}
              value={draft.title}
              disabled={!editable}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </Field>
          <Field label="Кратко для родителей">
            <AutoTextarea
              value={draft.summary}
              disabled={!editable}
              onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
            />
          </Field>
          <Field
            label={
              <span>
                Текст ·{' '}
                <span
                  className={
                    words < WORDS_MIN ? 'text-bad' : words > WORDS_MAX ? 'text-warn' : 'text-ok'
                  }
                >
                  {words} слов (нужно {WORDS_MIN}–{WORDS_MAX})
                </span>
              </span>
            }
          >
            <AutoTextarea
              value={draft.text}
              disabled={!editable}
              onChange={(e) => setDraft({ ...draft, text: e.target.value })}
              className="text-[17px]"
            />
          </Field>
          {editable && (
            <div className="sticky bottom-3 flex flex-wrap items-center gap-3 rounded-[16px] border border-line bg-card/95 p-3 shadow-lg backdrop-blur">
              {changed ? (
                <Badge tone="warn">Есть несохранённые изменения</Badge>
              ) : (
                <span className="text-[13px] text-muted">Все изменения сохранены</span>
              )}
              <span className="flex-1" />
              {changed && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDraft({ title: v.title, summary: v.summary, text: v.text })}
                >
                  Отменить правки
                </Button>
              )}
              <Button disabled={!changed || save.isPending} onClick={() => save.mutate()}>
                Сохранить
              </Button>
            </div>
          )}
          {save.error && <ErrorCard error={humanFromApi(save.error)} />}
        </CardContent>
      </Card>

      <Confirm
        open={confirmRevoice}
        onOpenChange={setConfirmRevoice}
        title={`Переозвучить ${LANG_LABEL[v.lang].toLowerCase()}?`}
        description={
          <>
            Будет потрачено ≈{credits(v.revoiceCredits)} ElevenLabs. Остальные языки не изменятся.
          </>
        }
        confirmLabel="Переозвучить"
        busy={revoice.isPending}
        onConfirm={() => revoice.mutate()}
      />
    </div>
  );
}

export function StoryPage({
  id,
  canEdit,
  isOwner,
}: {
  id: string;
  canEdit: boolean;
  isOwner: boolean;
}) {
  const q = useStory(id);
  const settings = useSettings();
  const readiness = useReadiness();
  const qc = useQueryClient();
  const [lang, setLang] = useState<Lang>('uz');
  const [timeOpen, setTimeOpen] = useState(false);
  const [time, setTime] = useState('');
  const [confirm, setConfirm] = useState<null | 'archive' | 'delete' | 'now'>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['story', id] });
    void qc.invalidateQueries({ queryKey: ['stories'] });
    void qc.invalidateQueries({ queryKey: ['readiness'] });
  };
  const act = useMutation({
    mutationFn: ({ path, body, method }: { path: string; body?: unknown; method?: string }) =>
      api<{ publishAt?: string }>(
        `/admin/stories/${id}${path}`,
        method === 'DELETE' ? { method } : { method: method ?? 'POST', body: body ?? {} },
      ),
    onSuccess: (r, v) => {
      const msg: Record<string, string> = {
        '/approve': r.publishAt ? `Запланирована на ${tashkent(r.publishAt)}` : 'Запланирована',
        '/publish-now': 'Опубликована — дети уже видят сказку',
        '/unschedule': 'Снята с расписания',
        '/archive': 'Убрана в архив',
        '': 'Сказка удалена',
      };
      toast.success(msg[v.path] ?? 'Готово');
      setTimeOpen(false);
      setConfirm(null);
      if (v.method === 'DELETE') go('stories');
      else refresh();
    },
  });

  if (q.isLoading) return <ListSkeleton rows={4} />;
  if (!q.data) return q.error ? <ErrorCard error={humanFromApi(q.error)} /> : null;
  const s = q.data;
  const version = s.versions.find((v) => v.lang === lang);
  const voiced = s.versions.length === 3 && s.versions.every((v) => v.audioUrl && !v.audioOutdated);
  const ready = voiced && s.unverified.length === 0;
  const notReadyWhy = ready
    ? null
    : [
        ...s.versions
          .filter((v) => !v.audioUrl || v.audioOutdated)
          .map((v) => (v.audioOutdated ? `переозвучьте ${v.lang}` : `ждём озвучку ${v.lang}`)),
        ...s.unverified.map((l) => `вычитайте ${l}-текст`),
      ].join(', ');
  const recommended = s.versions.filter((v) => v.proofread === 'recommended').map((v) => v.lang);
  const publishTime = settings.data?.defaultPublishTime ?? '18:00';
  const failedJob = s.jobs.find(
    (j) => j.status === 'FAILED' && !isStaleError(j, readiness.data, s.jobs),
  );
  const title = s.versions.find((v) => v.lang === 'ru')?.title ?? s.slug;

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        className="inline-flex items-center gap-1 self-start text-[14px] text-accent"
        onClick={() => go('stories')}
      >
        <ArrowLeft className="size-4" /> Сказки
      </button>

      <div className="flex flex-wrap items-start gap-4">
        <span
          className="size-20 shrink-0 rounded-[22px]"
          style={{
            background: `linear-gradient(140deg, hsl(${String(s.hue)} 80% 78%), hsl(${String((s.hue + 40) % 360)} 70% 62%))`,
          }}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <h1 className="text-[26px] font-bold tracking-tight">{title}</h1>
          <div className="flex flex-wrap items-center gap-2 text-[14px] text-muted">
            <StoryStatusBadge status={s.status} />
            <Badge>{SOURCE_LABEL[s.sourceType]}</Badge>
            {s.audioUploaded && <Badge tone="accent">аудио загружено вручную</Badge>}
            <span>
              {s.ageMin}–{s.ageMax} лет · оценка {s.qualityScore ?? '—'}
            </span>
            {s.theme && <span>· тема: {s.theme}</span>}
          </div>
          <Lifecycle status={s.status} />
        </div>
      </div>

      {s.flags.length > 0 && (
        <div className="flex flex-col gap-1 rounded-[16px] border border-warn/30 bg-warn/[.07] p-4">
          <p className="font-semibold">Проверьте внимательно:</p>
          <ul className="list-disc pl-5 text-[14px]">
            {s.flags.map((f, i) => (
              <li key={i}>
                {flagText(f)}
                {f.kind === 'similar' && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="text-accent"
                      onClick={() => go(`stories/${f.storyId}`)}
                    >
                      открыть
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {failedJob && (
        <ErrorCard
          error={humanFromJob(failedJob)}
          extra={
            <Button size="sm" variant="secondary" onClick={() => go('journal')}>
              Журнал
            </Button>
          }
        />
      )}

      {canEdit && s.status !== 'ARCHIVED' && (
        <Card>
          <CardContent className="flex flex-col gap-3 pt-5">
            <div className="flex flex-wrap gap-2">
              {(s.status === 'IN_REVIEW' || s.status === 'DRAFT') && (
                <Button
                  size="lg"
                  disabled={!ready || act.isPending}
                  onClick={() => act.mutate({ path: '/approve' })}
                >
                  <Send /> Одобрить и опубликовать в {publishTime}
                </Button>
              )}
              {s.status !== 'PUBLISHED' && (
                <Button
                  size="lg"
                  variant="outline"
                  disabled={!ready || act.isPending}
                  onClick={() => {
                    setTime(
                      toLocalInput(s.publishAt ?? new Date(Date.now() + 3600_000).toISOString()),
                    );
                    setTimeOpen(true);
                  }}
                >
                  <CalendarClock /> Выбрать время
                </Button>
              )}
              {s.status !== 'PUBLISHED' && (
                <Button
                  size="lg"
                  variant="outline"
                  disabled={!ready || act.isPending}
                  onClick={() => setConfirm('now')}
                >
                  <Rocket /> Опубликовать сейчас
                </Button>
              )}
              {s.status === 'SCHEDULED' && (
                <Button
                  size="lg"
                  variant="secondary"
                  disabled={act.isPending}
                  onClick={() => act.mutate({ path: '/unschedule' })}
                >
                  <Undo2 /> Снять с расписания
                </Button>
              )}
              <span className="flex-1" />
              <Button variant="ghost" onClick={() => setConfirm('archive')}>
                <Archive /> В архив
              </Button>
              {isOwner && s.status !== 'PUBLISHED' && (
                <Button variant="destructive" onClick={() => setConfirm('delete')}>
                  <Trash2 /> Удалить
                </Button>
              )}
            </div>
            {notReadyWhy && s.status !== 'PUBLISHED' && (
              <p className="text-[13px] text-muted">Публикация недоступна: {notReadyWhy}.</p>
            )}
            {recommended.length > 0 && s.status !== 'PUBLISHED' && (
              <p className="text-[13px] text-warn">
                Распознанный текст {recommended.join(', ')} ещё не вычитан — рекомендуем проверить.
              </p>
            )}
            {s.status === 'SCHEDULED' && (
              <p className="text-[14px]">Выйдет {tashkent(s.publishAt, true)} по Ташкенту.</p>
            )}
            {s.status === 'PUBLISHED' && (
              <p className="text-[14px]">
                Опубликована {tashkent(s.publishedAt, true)} по Ташкенту.
              </p>
            )}
            {act.error && <ErrorCard error={humanFromApi(act.error)} />}
          </CardContent>
        </Card>
      )}

      <Tabs value={lang} onValueChange={(v) => setLang(v as Lang)}>
        <TabsList aria-label="Язык">
          {s.versions.map((v) => (
            <TabsTrigger key={v.lang} value={v.lang}>
              {LANG_LABEL[v.lang]}{' '}
              {v.audioUrl && !v.audioOutdated ? '🔊' : v.audioOutdated ? '⚠️' : '…'}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {version && (
        <VersionPanel
          key={`${version.lang}-${version.title}-${version.summary}-${version.text}`}
          storyId={id}
          v={version}
          story={s}
          onSaved={refresh}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Задачи и стоимость</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-[14px]">
          <p>
            Итого: {credits(s.cost.ttsCredits)} ElevenLabs ·{' '}
            {s.cost.claudeInputTokens + s.cost.claudeOutputTokens} токенов Claude ·{' '}
            {money(s.cost.money)}
          </p>
          <ul className="flex flex-col gap-1">
            {s.jobs.map((j) => (
              <li key={j.id} className="flex flex-wrap items-center gap-2">
                <JobStatusBadge status={j.status} stopping={j.cancelRequested} />
                <span>
                  {JOB_LABEL[j.type] ?? j.type} {j.lang ?? ''}
                </span>
                <span className="text-muted">
                  · {tashkent(j.createdAt)} · {credits(j.ttsCredits)}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Dialog open={timeOpen} onOpenChange={setTimeOpen}>
        <DialogContent>
          <DialogTitle>Когда опубликовать?</DialogTitle>
          <DialogDescription>Время по Ташкенту.</DialogDescription>
          <input
            type="datetime-local"
            className={cn(inputClass, 'mt-4')}
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setTimeOpen(false)}>
              Отмена
            </Button>
            <Button
              disabled={!time || act.isPending}
              onClick={() =>
                act.mutate({ path: '/approve', body: { publishAt: fromLocalInput(time) } })
              }
            >
              Запланировать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Confirm
        open={confirm === 'now'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Опубликовать сейчас?"
        description="Сказка сразу появится у детей в приложении."
        confirmLabel="Опубликовать"
        busy={act.isPending}
        onConfirm={() => act.mutate({ path: '/publish-now' })}
      />
      <Confirm
        open={confirm === 'archive'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Убрать в архив?"
        description="Сказка пропадёт из приложения и из очереди проверки."
        confirmLabel="В архив"
        destructive
        busy={act.isPending}
        onConfirm={() => act.mutate({ path: '/archive' })}
      />
      <Confirm
        open={confirm === 'delete'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Удалить сказку навсегда?"
        description="Тексты и озвучка будут удалены. Это нельзя отменить."
        confirmLabel="Удалить"
        destructive
        busy={act.isPending}
        onConfirm={() => act.mutate({ path: '', method: 'DELETE' })}
      />
    </div>
  );
}
